import { useEffect, useMemo, useState } from 'react';
import '../../styles/test-system.css';
import { parseUTCToLocal } from '../../utils/dateUtils';

/**
 * Renders a remaining span as the coarsest useful pair of units: days and hours
 * far out, seconds only in the last minute, so a countdown a week away is not a
 * wall of digits.
 * @param {number} milliseconds - Remaining time
 * @returns {string} - e.g. "2d 3h", "3h 12m", "4m 07s", "9s"
 */
export const formatCountdown = (milliseconds) => {
    const total = Math.max(Math.floor(milliseconds / 1000), 0);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
    return `${seconds}s`;
};

/**
 * A ticking clock the page can compute state from, so a deadline that passes
 * while the page is open closes the CTAs with it instead of needing a reload.
 */
export const useNow = (intervalMs = 1000) => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);

    return now;
};

const HOUR_MS = 60 * 60 * 1000;

const Countdown = ({ target, prefix = 'in', passedLabel = 'passed' }) => {
    const targetTime = useMemo(() => parseUTCToLocal(target)?.getTime() ?? null, [target]);
    const now = useNow();

    if (targetTime === null) return null;

    const remaining = targetTime - now;
    if (remaining <= 0) {
        return <span className="ts-countdown ts-countdown--passed">{passedLabel}</span>;
    }

    return (
        <span className={`ts-countdown${remaining < HOUR_MS ? ' ts-countdown--soon' : ''}`}>
            {prefix ? `${prefix} ` : ''}{formatCountdown(remaining)}
        </span>
    );
};

export default Countdown;
